import { useMemo, useState } from "react";
import { DefaultCheckBox } from "../../../../../../common/components/default_checkbox";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { DefaultDateRangePickerInput } from "../../../../../../common/components/default_date_range_picker_input";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";
import { MaskedInput } from "../../../../../../common/components/masked_inputs/masked_input";
import { ClienteDropDown } from "../../../../../../common/domain/models/cliente_dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { CategoriaAssistenciaDropDown } from "../../../../domain/models/categoria_assistencia_dropdown";

import CascateList from "../../../../../../common/components/cascate_list";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultInput from "../../../../../../common/components/default_input";

/**
 * Header
 */

type Props = {
  idCliente: number[] | null;
  cliente: string | null;
  centroCustos: number[] | null;
  local: string | null;
  dataAberturaDe: Date | null;
  dataAberturaAte: Date | null;
  tipo: number | null;
  dataEncerramentoDe: Date | null;
  dataEncerramentoAte: Date | null;
  codigo: number | null;
  procedente: number | null;
  categorias: number[] | null;
  resolvido: number | null;
  prioridade: number | null;
  nroChamado: string | null;
  status: number | null;
  clientesDropdown: ClienteDropDown[];
  centroCustosDropdown: CentroCustoDropDown[];
  categoriasDropdown: CategoriaAssistenciaDropDown[];
  onIdClienteChange: (value: number[] | null) => void;
  onClienteChange: (value: string | null) => void;
  onCentroCustosChange: (value: number[] | null) => void;
  onLocalChange: (value: string | null) => void;
  onDataAberturaChange: (de: Date | null, ate: Date | null) => void;
  onTipoChange: (value: number | null) => void;
  onDataEncerramentoChange: (de: Date | null, ate: Date | null) => void;
  onCodigoChange: (value: number | null) => void;
  onProcedenteChange: (value: number | null) => void;
  onCategoriasChange: (value: number[] | null) => void;
  onResolvidoChange: (value: number | null) => void;
  onPrioridadeChange: (value: number | null) => void;
  onNroChamadoChange: (value: string | null) => void;
  onStatusChange: (value: number | null) => void;
  onClearFilters: () => void;
};

export const Header = ({
  idCliente,
  cliente,
  centroCustos,
  local,
  dataAberturaDe,
  dataAberturaAte,
  tipo,
  dataEncerramentoDe,
  dataEncerramentoAte,
  codigo,
  procedente,
  categorias,
  resolvido,
  prioridade,
  nroChamado,
  status,
  clientesDropdown,
  centroCustosDropdown,
  categoriasDropdown,
  onIdClienteChange,
  onClienteChange,
  onCentroCustosChange,
  onLocalChange,
  onDataAberturaChange,
  onTipoChange,
  onDataEncerramentoChange,
  onCodigoChange,
  onProcedenteChange,
  onCategoriasChange,
  onResolvidoChange,
  onPrioridadeChange,
  onNroChamadoChange,
  onStatusChange,
  onClearFilters,
}: Props) => {
  const [fill, setFill] = useState(false);

  const selectedClientes = useMemo(() => {
    if (!idCliente) return null;
    return clientesDropdown.filter(x => idCliente.includes(x.id));
  }, [
    idCliente,
    clientesDropdown,
  ]);

  const selectedCentroCustos = useMemo(() => {
    if (!centroCustos) return null;
    return centroCustosDropdown.filter(x => centroCustos.includes(x.id));
  }, [
    centroCustos,
    centroCustosDropdown,
  ]);

  const selectedCategorias = useMemo(() => {
    if (!categorias) return null;
    return categoriasDropdown.filter(x => categorias.includes(x.idCategoriaAssistencia));
  }, [
    categorias,
    categoriasDropdown,
  ]);

  const statusOptions = useMemo(() => {
    return [
      { id: 0, label: 'Todas' },
      { id: 1, label: 'Abertas' },
      { id: 2, label: 'Encerradas' },
    ];
  }, [
  ]);

  const tipoOptions = useMemo(() => {
    return [
      { id: 2, label: 'Todas' },
      { id: 0, label: 'Interna' },
      { id: 1, label: 'Externa' },
    ];
  }, [
  ]);

  const procedenteOptions = useMemo(() => {
    return [
      { id: 3, label: 'Todas' },
      { id: 0, label: 'Aguardando Vistoria' },
      { id: 1, label: 'Sim' },
      { id: 2, label: 'Não' },
    ];
  }, [
  ]);

  const resolvidoOptions = useMemo(() => {
    return [
      { id: 4, label: 'Todas' },
      { id: 5, label: 'Todas Em Aberto' },
      { id: 0, label: 'Aguardando Reparo' },
      { id: 3, label: 'Reparo Iniciado' },
      { id: 1, label: 'Sim' },
      { id: 2, label: 'Não' },
    ];
  }, [
  ]);

  const prioridadeOptions = useMemo(() => {
    return [
      { id: 3, label: 'Todas' },
      { id: 0, label: 'Baixa' },
      { id: 1, label: 'Média' },
      { id: 2, label: 'Alta' },
    ];
  }, [
  ]);

  return (
    <CascateList title='Filtros' defaultOpen={true}>
      <div className="w-full flex flex-col gap-2">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-2 gap-y-4">
          <div className="w-full flex flex-col gap-1">
            <div className="min-h-[2.85rem]">
              {
                fill ? (
                  <DefaultInput
                    name='cliente'
                    label='Cliente'
                    value={cliente}
                    onChangeValue={x => onClienteChange(x)}
                  />
                ) : (
                  <DefaultMultiAutocomplete
                    label='Cliente'
                    options={clientesDropdown}
                    getOptionKey={x => x.id}
                    getOptionLabel={x => x.title}
                    value={selectedClientes}
                    onChange={x => onIdClienteChange(x !== null ? x.map(i => i.id) : null)}
                  />
                )
              }
            </div>

            <DefaultCheckBox
              name='cliente_preenchido'
              label='Cliente preenchido'
              value={fill}
              onChangeValue={e => {
                onIdClienteChange(null);
                onClienteChange(null);
                setFill(e);
              }}
            />
          </div>

          <DefaultMultiAutocomplete
            label='Centro de Custo'
            options={centroCustosDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedCentroCustos}
            onChange={x => onCentroCustosChange(x !== null ? x.map(i => i.id) : null)}
          />

          <DefaultInput
            name='local'
            label='Local'
            value={local}
            onChangeValue={x => onLocalChange(x)}
          />

          <DefaultDateRangePickerInput
            label='Data Abertura'
            dataDe={dataAberturaDe}
            dataAte={dataAberturaAte}
            onChange={(de, ate) => {
              onDataAberturaChange(de, ate);
            }}
          />

          <DefaultSelectInput
            name='tipo'
            label='Tipo'
            showSelectionLabel={false}
            options={tipoOptions}
            value={tipo}
            onChangeValue={x => onTipoChange(Number(x))}
          />

          <DefaultDateRangePickerInput
            label='Data Encerramento'
            dataDe={dataEncerramentoDe}
            dataAte={dataEncerramentoAte}
            onChange={(de, ate) => {
              onDataEncerramentoChange(de, ate);
            }}
          />

          <MaskedInput
            name='codigo'
            label='Código Sistema'
            mask='00000000'
            maskPlaceholder='________'
            value={codigo !== null ? String(codigo) : ''}
            onChange={x => onCodigoChange(x !== '' ? Number(x) : null)}
          />

          <DefaultInput
            name='nroChamado'
            label='Nº do Chamado'
            value={nroChamado}
            onChangeValue={x => onNroChamadoChange(x)}
          />

          <DefaultSelectInput
            name='status'
            label='Status'
            showSelectionLabel={false}
            options={statusOptions}
            value={status}
            onChangeValue={x => onStatusChange(Number(x))}
          />

          <DefaultSelectInput
            name='procedente'
            label='Procedente?'
            showSelectionLabel={false}
            options={procedenteOptions}
            value={procedente}
            onChangeValue={x => onProcedenteChange(Number(x))}
          />

          <DefaultMultiAutocomplete
            label='Categorias'
            options={categoriasDropdown}
            getOptionKey={x => x.idCategoriaAssistencia}
            getOptionLabel={x => x.descricao}
            value={selectedCategorias}
            onChange={x => onCategoriasChange(x !== null ? x.map(i => i.idCategoriaAssistencia) : null)}
          />

          <DefaultSelectInput
            name='resolvido'
            label='Resolvido?'
            showSelectionLabel={false}
            options={resolvidoOptions}
            value={resolvido}
            onChangeValue={x => onResolvidoChange(Number(x))}
          />

          <DefaultSelectInput
            name='prioridade'
            label='Prioridade'
            showSelectionLabel={false}
            options={prioridadeOptions}
            value={prioridade}
            onChangeValue={x => onPrioridadeChange(Number(x))}
          />
        </div>

        <div className="w-full flex justify-end">
          <div className="w-[180px]">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
              buttonColor="yellow"
              onClick={onClearFilters}>
              Limpar filtros
            </PrimaryButton>
          </div>
        </div>
      </div>
    </CascateList>
  );
}